<template>
  <div class="order">
    <div class="webkit-gap"></div>
    <NavHeader
      :title="$t('screens.order.headerTitle')"
      :subtitle="$t('screens.order.headerSubtitle', { tableName })"
      :cta="tableOrdersCta"
      @onBackClick="goBack"
    />
    <TableOrdersPicker
      v-model="tableOrdersPickerOpen"
      :orders="tableOrders"
      :selected="uniqueOrderNumber"
      @select="switchToOrder"
    />
    <div v-if="isLoading" class="py-6 d-flex justify-center align-center">
      <v-progress-circular indeterminate color="black" />
    </div>
    <div v-else style="height: 100%;">
      <LayoutCard
        v-show="totalAmount"
        :loading="isLoading"
        :title="$t('screens.order.summaryTitle')"
        rounded
      >
        <PriceSummaryPayGo
          :subtotal-amount="subtotalAmount"
          :subtotal-without-discount="
            order?.subtotal_before_discounts || order?.subtotal
          "
          :total-amount="totalAmount"
          :transaction-fee="transactionFeeAmount"
          :paid-amount="paidAmount"
          :payments="payments"
          :loyalty-program="loyaltyProgram"
          :loyalty-discount-amount="loyaltyDiscountAmount"
        />
        <v-card
          v-if="hasUnpaidOrderItems && partlyPaidByWaiter"
          color="#f5a62333"
          class="d-flex pa-4 mt-6"
          style="border-radius: 20px;"
          flat
          data-cy="pay-go-partly-paid-notice"
        >
          <v-icon color="#C77700" class="mr-4">mdi-information</v-icon>
          <div class="d-flex flex-column">
            <span class="tp-text-body font-weight-bold">
              {{ $t('screens.payment.error.partAlreadyPaid.title') }}
            </span>
            <span class="tp-text-body">
              {{ $t('screens.payment.error.partAlreadyPaid.description') }}
            </span>
          </div>
        </v-card>
        <v-card
          v-if="!hasUnpaidOrderItems && allItemsPaid"
          color="#489f3133"
          class="d-flex pa-4 mt-6"
          style="border-radius: 20px;"
          flat
        >
          <v-icon color="#489F31" class="mr-4">mdi-check-circle</v-icon>
          <div>
            <span class="tp-text-body green-text">
              {{ $t('screens.payGoOrder.allPaidMessage') }}
            </span>
          </div>
        </v-card>
      </LayoutCard>
      <LayoutCard v-if="!hasOrderItems" grow>
        <StateScreen
          :title="$t('screens.order.emptyStateTitle')"
          :subtitle="$t('screens.order.emptyStateSubtitle')"
          :image="require(`@/assets/images/states/empty.svg`)"
        />
        <div class="mt-auto"></div>
        <div v-if="!hasOrderItems" class="py-4 px-6">
          <Button dark @click="refreshOrder">
            {{ $t('screens.payGoOrder.refreshCta') }}
          </Button>
          <div class="my-3"></div>
          <Button
            v-if="!$store.getters.customizationSelectScreenHideViewMenuButton"
            gray
            @click="goBack"
          >
            {{ $t('screens.payGoOrder.backCta') }}
          </Button>
        </div>
      </LayoutCard>
      <OrderLoyaltyStatusCard
        v-if="hasOrderItems && !allItemsPaid"
        :unique-order-number="uniqueOrderNumber"
        :loyalty-discount-amount="loyaltyDiscountAmount"
        redirect="Order"
      />
      <LayoutCard
        v-if="hasUnpaidOrderItems"
        :loading="isLoading"
        :title="
          hasPaidOrderItems
            ? $t('screens.order.orderListLeftUnpaidProductTitle')
            : $t('screens.order.orderListTitle')
        "
      >
        <div
          v-for="(item, key) in unpaidOrderItems"
          :key="key"
          class="order-item"
          :class="{
            'pb-4': key + 1 < unpaidOrderItems.length,
            'pt-1': key == 0,
          }"
        >
          <OrderItem
            :item-key="key"
            :name="item.name"
            :price="item.price"
            :quantity="item.quantity"
            :modifiers="item.modifiers"
            :is-paid="item.isPaid"
            :selected="item.otherSelected"
            :show-only-paid-modifiers="showPaidModifiersEnabled"
            :hide-modifiers="!showPaidModifiersEnabled"
            :strikethrough-price="item.strikethroughPrice"
            @click="navigateToItemSelection"
          />
        </div>
      </LayoutCard>
      <LayoutCard
        v-if="hasPaidOrderItems"
        :loading="isLoading"
        :title="$t('screens.order.orderListPaidProductTitle')"
        rounded
      >
        <div
          v-for="(item, key) in paidOrderItems"
          :key="key"
          class="order-item"
          :class="{ 'pb-4': key + 1 < paidOrderItems.length, 'pt-1': key == 0 }"
        >
          <OrderItem
            :item-key="key"
            :name="item.name"
            :price="item.price"
            :quantity="item.quantity"
            :modifiers="item.modifiers"
            :is-paid="item.isPaid"
            :show-only-paid-modifiers="showPaidModifiersEnabled"
            :hide-modifiers="!showPaidModifiersEnabled"
            :strikethrough-price="item.strikethroughPrice"
          />
        </div>
      </LayoutCard>
      <footer v-if="hasUnpaidOrderItems">
        <Button
          v-if="hasOrderItems"
          :disabled="allItemsPaid || applyingDiscount || partlyPaidByWaiter"
          :loading="sliptButtonLoading"
          data-cy="pay-go-split-cta"
          gray
          @click="navigateToItemSelection"
        >
          <v-icon small color="black" class="mr-1">mdi-call-split</v-icon>
          {{ $t('screens.payGoOrder.payForProductsCta') }}
        </Button>
        <Button
          v-if="hasOrderItems"
          :disabled="allItemsPaid || applyingDiscount || partlyPaidByWaiter"
          :loading="payButtonLoading"
          data-cy="pay-go-pay-all-cta"
          dark
          @click="navigateToPayment"
        >
          <v-icon small color="white" class="mr-1">mdi-arrow-right</v-icon>
          {{ $t('screens.payGoOrder.payAllCta') }}
        </Button>
      </footer>
    </div>
  </div>
</template>

<script>
import LayoutCard from '@/components/LayoutCard.vue';
import NavHeader from '@/components/Navigation/NavHeader.vue';
import OrderItem from '@/components/Orders/OrderItem.vue';
import StateScreen from '@/components/StateScreen.vue';
import PriceSummaryPayGo from '@/components/common/PriceSummaryPayGo.vue';
import Button from '@/components/Button.vue';
import OrderLoyaltyStatusCard from '@/components/Loyalty/OrderLoyaltyStatusCard.vue';
import TableOrdersPicker from '@/components/Orders/TableOrdersPicker.vue';
import { PRESENCE_STATES } from '@/store/modules/orderPresence';

export default {
  components: {
    LayoutCard,
    NavHeader,
    OrderItem,
    StateScreen,
    Button,
    PriceSummaryPayGo,
    OrderLoyaltyStatusCard,
    TableOrdersPicker,
  },
  data() {
    return {
      isLoading: false,
      uniqueOrderNumber: null,
      presenceOwnerId: `presence-${Date.now()}-${Math.random()}`,
      sliptButtonLoading: false,
      payButtonLoading: false,
      fetchOrdersInterval: null,
      otherSelectedOptions: [],
      tableOrdersPickerOpen: false,
    };
  },
  computed: {
    session() {
      return this.$store.state.session;
    },
    tableName() {
      return this.$store.state.table.name;
    },
    hasOrderItems() {
      return this.orderItems.length > 0;
    },
    hasPaidOrderItems() {
      return this.orderItems.filter(orderItem => orderItem.isPaid).length > 0;
    },
    hasUnpaidOrderItems() {
      return this.orderItems.filter(orderItem => !orderItem.isPaid).length > 0;
    },
    orderStatusClosed() {
      return this.order?.status === 'closed';
    },
    order() {
      return this.$store.getters.getPOSOrderByUniqueOrderNumber(
        this.uniqueOrderNumber
      );
    },
    orderItems() {
      if (!this.order) {
        return [];
      }
      return (
        this.order.items.map(item => {
          return {
            id: item.id,
            name: item.name,
            quantity: item.quantity,
            modifiers: item.modifiers || [],
            price: item.price,
            isPaid:
              item.is_paid || this.order.is_paid || this.orderStatusClosed,
            otherSelected: this.otherSelectedOptions.includes(item.id),
            strikethroughPrice: item.strikethrough_price,
          };
        }) || []
      );
    },
    unpaidOrderItems() {
      return this.orderItems.filter(item => !item.isPaid);
    },
    paidOrderItems() {
      return this.orderItems.filter(item => item.isPaid);
    },
    subtotalAmount() {
      return this?.order?.subtotal;
    },
    subtotalWithoutDiscount() {
      return this?.order?.subtotal_before_discounts;
    },
    totalAmount() {
      return this?.order?.total;
    },
    paidAmount() {
      return this.orderStatusClosed
        ? this.subtotalAmount
        : this?.order?.payments_sum;
    },
    transactionFeeAmount() {
      return this?.order?.transaction_fee;
    },
    payments() {
      return this?.order?.payments;
    },
    allItemsPaid() {
      return this.orderItems.every(item => item.isPaid);
    },
    // A coupon or payment on the till that Greet didn't make: the guest has to finish paying with the waiter
    partlyPaidByWaiter() {
      return Boolean(this.order?.paid_outside_greet);
    },
    // The orders a guest can switch between, oldest first: every open one on the table, the ones paid in Greet
    // during this visit (they stay so the guest keeps sight of what was paid), and the one on screen.
    // Orders the till closed or deleted without a Greet payment drop out.
    tableOrders() {
      return this.$store.getters.POSOrders.filter(
        order =>
          order.status !== 'closed' ||
          order.payments?.length > 0 ||
          order.unique_order_number === this.uniqueOrderNumber
      ).sort(
        (a, b) =>
          new Date(a.placed_at || a.created_at) -
          new Date(b.placed_at || b.created_at)
      );
    },
    // A header icon, counting the orders still to pay, that opens the picker; none while the table has one order
    tableOrdersCta() {
      if (this.tableOrders.length < 2) {
        return null;
      }

      return {
        icon: 'mdi-receipt-text-outline',
        badge: this.tableOrders.filter(
          order => order.status !== 'closed' && !order.is_paid
        ).length,
        dataCy: 'pay-go-table-orders',
        onClick: () => {
          this.tableOrdersPickerOpen = true;
        },
      };
    },
    selectedMenuItems() {
      return this.$store.getters.getSelectedMenuItems;
    },
    loyaltyProgramEnabled() {
      return this.$store.getters.functionalityLoyaltyProgramEnabled;
    },
    loyaltyProgram() {
      return this.$store.getters.enrolledRestaurantLoyaltyProgram;
    },
    showPaidModifiersEnabled() {
      return this.$store.state.restaurant?.functionalities?.includes(
        'show-paid-modifiers'
      );
    },
    loyaltyDiscountAmount() {
      return this.subtotalWithoutDiscount - this.subtotalAmount;
    },
    applyingDiscount() {
      return this.$store.getters.getApplyingLoyaltyDiscountToOrder;
    },
    leftToPay() {
      const amount = this.subtotalAmount - this.paidAmount;
      return amount.toFixed(2);
    },
    restaurantId() {
      return this.$store.getters.getCurrentRestaurantId;
    },
  },
  watch: {
    uniqueOrderNumber(newValue, oldValue) {
      if (oldValue) {
        this.$store.dispatch('leaveOrderPresence', {
          uniqueOrderNumber: oldValue,
          ownerId: this.presenceOwnerId,
        });
      }
      if (newValue) {
        this.$store.dispatch('joinOrderPresence', {
          uniqueOrderNumber: newValue,
          state: PRESENCE_STATES.VIEWING,
          ownerId: this.presenceOwnerId,
        });
      }
    },
  },
  async mounted() {
    this.isLoading = true;

    const sessionToken = this.$route.params.session;
    await this.$store.dispatch('fetchSessionData', { session: sessionToken });
    await this.getOrder();

    this.isLoading = false;

    if (this.uniqueOrderNumber) {
      this.$store.dispatch('deselectAllOrderItems', {
        uniqueOrderNumber: this.uniqueOrderNumber,
      });
    }
    this.$store.dispatch('subscribeToPusherChannels');

    this.fetchPreviouslySelectedOptions();

    window.addEventListener(
      'order-items-selected',
      this.fetchPreviouslySelectedOptions
    );

    if (this.hasUnpaidOrderItems) {
      this.$store.dispatch('sendAnalyticsEvent', {
        event: 'app_pay-go_item-select-screen',
        restaurant_id: this.restaurantId,
      });
    }
  },
  beforeDestroy() {
    clearInterval(this.fetchOrdersInterval);

    this.$store.dispatch('leaveOrderPresence', {
      uniqueOrderNumber: this.uniqueOrderNumber,
      ownerId: this.presenceOwnerId,
    });

    window.removeEventListener(
      'order-items-selected',
      this.fetchPreviouslySelectedOptions
    );
  },
  methods: {
    async navigateToPayment() {
      this.payButtonLoading = true;
      const [canPay, unpaidItemsSelectedByOthers] = await Promise.all([
        this.checkCanPayForOrder(),
        this.checkIfUnpaidItemsAreSelectedByOthers(),
      ]);

      if (!canPay || unpaidItemsSelectedByOthers) {
        this.payButtonLoading = false;
        return;
      }

      const onSuccess = () => {
        this.$router.push({
          name: 'Pay',
          params: {
            session: this.session,
            order: this.order.unique_order_number,
          },
          query: {
            payForSelectedItems: true,
            ref: 'order',
            userSubtotalAmount: this.leftToPay,
          },
        });
      };
      await this.$store.dispatch('selectAllUnpaidOrderItems', {
        uniqueOrderNumber: this.uniqueOrderNumber,
        onSuccess,
      });
      this.payButtonLoading = false;
    },
    goBack() {
      this.$router.replace({
        name: 'Select',
        params: { session: this.session },
      });
    },
    navigateToMenu() {
      this.$router.push({
        name: 'Menu',
        params: { session: this.session },
      });
    },
    async checkCanPayForOrder() {
      const canPay = await this.$store.dispatch('checkCanPayForOrder', {
        uniqueOrderNumber: this.uniqueOrderNumber,
      });

      return canPay;
    },
    async checkIfUnpaidItemsAreSelectedByOthers() {
      return await this.$store.dispatch(
        'checkIfUnpaidItemsAreSelectedByOthers',
        {
          uniqueOrderNumber: this.uniqueOrderNumber,
        }
      );
    },
    async getOrder({ force = false } = {}) {
      await this.$store.dispatch('fetchOrdersFromPOS');
      const orderIdFromQuery = this.$route.query.orderNumber;
      // The oldest open order first, as when Greet only knew one order per table
      const firstOpenOrderId =
        this.tableOrders.find(order => order.status !== 'closed')
          ?.unique_order_number || null;

      if (force) {
        this.uniqueOrderNumber = firstOpenOrderId;
      } else {
        this.uniqueOrderNumber = orderIdFromQuery || firstOpenOrderId;
      }

      if (this.uniqueOrderNumber) {
        this.$router
          .replace({ query: { orderNumber: this.uniqueOrderNumber } })
          .catch(() => {});
      }

      this.initiateFetchOrdersInterval();
    },
    async refreshOrder() {
      this.isLoading = true;
      await this.getOrder({ force: true });
      this.isLoading = false;
    },
    switchToOrder(uniqueOrderNumber) {
      if (uniqueOrderNumber === this.uniqueOrderNumber) {
        return;
      }

      // Presence moves with it: the uniqueOrderNumber watcher leaves the old order and joins this one
      this.uniqueOrderNumber = uniqueOrderNumber;
      this.$router
        .replace({ query: { orderNumber: uniqueOrderNumber } })
        .catch(() => {});

      this.$store.dispatch('deselectAllOrderItems', { uniqueOrderNumber });
      this.fetchPreviouslySelectedOptions();
    },
    async navigateToItemSelection() {
      this.sliptButtonLoading = true;
      const canPay = await this.checkCanPayForOrder();

      if (!canPay) {
        this.sliptButtonLoading = false;
        return;
      }

      this.$router.push({
        name: 'SelectItems',
        query: { orderNumber: this.uniqueOrderNumber },
      });
      this.sliptButtonLoading = false;
    },
    initiateFetchOrdersInterval() {
      // While user is in pay&go order page, we should repeatedly fetch order (for updates)
      // each 15 seconds.
      if (this.hasOrderItems && this.fetchOrdersInterval === null && !this.orderStatusClosed) {
        this.fetchOrdersInterval = setInterval(() => {
          if (this.applyingDiscount) {
            return;
          }
          this.$store.dispatch('fetchOrdersFromPOS');
        }, 15000);
      }
    },
    async fetchPreviouslySelectedOptions() {
      if (!this.uniqueOrderNumber) {
        return;
      }

      const uniqueOrderNumber = this.uniqueOrderNumber;
      const selectedOrderItems = await this.$store.dispatch(
        'fetchSelectedOrderItems',
        {
          uniqueOrderNumber,
        }
      );
      // The guest may have switched order while this was loading; a failed request must not break the item list
      if (uniqueOrderNumber === this.uniqueOrderNumber) {
        this.otherSelectedOptions = selectedOrderItems?.others_selected || [];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.order {
  background-color: $color-background-gray !important;
  display: flex;
  flex-flow: column;
  min-height: 100%;
  padding-bottom: 100px;
}

footer {
  position: fixed;
  width: 100%;
  max-width: $max-app-width;
  display: flex;
  gap: 1em;
  justify-content: space-between;
  padding: 2em 1em calc(2em + env(safe-area-inset-bottom)) 1em;
  bottom: 0px;
  background-image: linear-gradient(
    to top,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 1) 30%,
    rgba(0, 212, 255, 0) 100%
  );
}

.order-item {
  display: flex;
  justify-content: space-between;
}

.green-text {
  color: #0e3d02;
}
</style>
